import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import cookieService from '../services/cookieService';
import { trackEvent, trackPageView } from '../utils/analytics';
import { isTrackingBlocked, safeLogError, enhancedLogError, safeStorage, safeCookies } from '../utils/trackingUtils';
import { logger } from "../utils/logger";
import { secureStorage } from '../utils/secureStorage';
// import { logger } from "../utils/logger";
interface UseCookieTrackingOptions {
    trackPageViews?: boolean;
    trackUserInteractions?: boolean;
    enableTrainingProgress?: boolean;
    trackSearchQueries?: boolean;
    trackSessionEvents?: boolean;
    autoCreateSession?: boolean;
}
interface TrackingEvent {
    type: string;
    data: any;
    timestamp: number;
}
export const useCookieTracking = (options: UseCookieTrackingOptions = {}) => {
    const location = useLocation();
    const sessionRef = useRef<any>(null);
    const lastActivityRef = useRef<number>(Date.now());
    const eventQueueRef = useRef<TrackingEvent[]>([]);
    const trackingBlockedRef = useRef<boolean>(false);
    const { trackPageViews = true, trackUserInteractions = true, enableTrainingProgress = true, trackSearchQueries = true, trackSessionEvents = true, autoCreateSession = true } = options;
    // Check if tracking is blocked
    const checkTrackingBlocked = useCallback(() => {
        return isTrackingBlocked();
    }, []);
    // Process event queue - moved here to avoid temporal dead zone
    const processEventQueue = useCallback(() => {
        if (eventQueueRef.current.length > 0 && !trackingBlockedRef.current) {
            // Send high-priority events to analytics service
            eventQueueRef.current.forEach(event => {
                try {
                    // Send high-priority events to external analytics
                    if (['button_click', 'form_submit', 'video_play', 'video_pause', 'training_progress', 'search_query'].includes(event.type)) {
                        trackEvent({
                            action: event.type,
                            category: 'user_interaction',
                            label: event.data?.context || event.type,
                            custom_parameters: {
                                ...event.data,
                                timestamp: event.timestamp,
                                source: 'cookie_tracking',
                                session_id: sessionRef.current?.id
                            }
                        });
                    }
                }
                catch (error) {
                    // Use enhanced logging to handle tracking prevention gracefully
                    enhancedLogError('Failed to send event to analytics service:', error, 'useCookieTracking');
                }
            });
            // Clear the queue after processing
            eventQueueRef.current = [];
        }
    }, []);
    // Initialize session tracking
    useEffect(() => {
        if (autoCreateSession && !trackingBlockedRef.current) {
            try {
                // Check if tracking is blocked before attempting to create session
                if (checkTrackingBlocked()) {
                    trackingBlockedRef.current = true;
                    // Store tracking state in safe storage
                    safeStorage.setItem('trackingDisabled', 'true');
                    safeStorage.setItem('trackingDisabledReason', 'session_creation_failed');
                    safeStorage.setItem('trackingDisabledTimestamp', new Date().toISOString());
                    return;
                }
                const existingSession = cookieService.getSessionData();
                if (!existingSession) {
                    sessionRef.current = cookieService.createSession();
                }
                else {
                    sessionRef.current = existingSession;
                }
            }
            catch (error) {
                // Check if this is a tracking prevention error
                if (error instanceof DOMException && (error.name === 'SecurityError' ||
                    error.message.includes('storage') ||
                    error.message.includes('blocked'))) {
                    trackingBlockedRef.current = true;
                    // Store tracking state in safe storage
                    safeStorage.setItem('trackingDisabled', 'true');
                    safeStorage.setItem('trackingDisabledReason', 'session_creation_error');
                    safeStorage.setItem('trackingDisabledTimestamp', new Date().toISOString());
                    return;
                }
                // Use safe logging to only show non-tracking-prevention errors
                safeLogError('Failed to initialize session tracking:', error);
                // If we get a JSON parsing error, clear cookies and create new session
                if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message.includes('Unterminated string')) {
                    // Use safe logging to only show non-tracking-prevention errors
                    safeLogError('Detected corrupted cookie, clearing and creating new session', null);
                    try {
                        cookieService.clearAllCookies();
                        sessionRef.current = cookieService.createSession();
                    }
                    catch (createError) {
                        // Use safe logging to only show non-tracking-prevention errors
                        safeLogError('Failed to create new session after clearing cookies:', createError);
                        // Disable tracking if we can't create a session
                        sessionRef.current = null;
                    }
                }
                else {
                    // Create a new session if there's an error
                    try {
                        sessionRef.current = cookieService.createSession();
                    }
                    catch (createError) {
                        // Use safe logging to only show non-tracking-prevention errors
                        safeLogError('Failed to create new session:', createError);
                    }
                }
            }
        }
    }, [autoCreateSession, checkTrackingBlocked]);
    // Track page views
    useEffect(() => {
        if (trackPageViews && sessionRef.current && !trackingBlockedRef.current) {
            try {
                const currentPath = location.pathname;
                // Update session with page view
                cookieService.incrementPageViews();
                // Track page visit in behavior
                cookieService.trackPageVisit(currentPath);
                // Update last activity
                lastActivityRef.current = Date.now();
                // Track session event
                if (trackSessionEvents) {
                    cookieService.trackSessionEvent('page_view');
                }
                // Send page view to analytics service with enhanced context
                try {
                    const sessionData = cookieService.getSessionData();
                    let behaviorData = null;
                    // Safely get user behavior data with error handling
                    try {
                        behaviorData = cookieService.getUserBehavior();
                    }
                    catch (behaviorError) {
                        logger.warn('Failed to get user behavior data:', { "arg0": behaviorError });
                        // Continue without behavior data
                    }
                    trackPageView(currentPath, window.location.href);
                    // Track enhanced page view event with cookie context
                    trackEvent({
                        action: 'page_view',
                        category: 'navigation',
                        label: currentPath,
                        custom_parameters: {
                            path: currentPath,
                            full_url: window.location.href,
                            session_id: sessionData?.session_id,
                            page_views_count: sessionData?.page_views || 0,
                            time_on_site: behaviorData?.time_spent_on_site || 0,
                            source: 'cookie_tracking'
                        }
                    });
                }
                catch (error) {
                    // Use safe logging to only show non-tracking-prevention errors
                    safeLogError('Failed to send page view to analytics service:', error);
                }
            }
            catch (error) {
                // Check if this is a tracking prevention error
                if (error instanceof DOMException && (error.name === 'SecurityError' ||
                    error.message.includes('storage') ||
                    error.message.includes('blocked'))) {
                    trackingBlockedRef.current = true;
                    // Tracking prevention blocked - no logging needed
                    return;
                }
                // Use safe logging to only show non-tracking-prevention errors
                safeLogError('Failed to track page view:', error);
                // Don't let cookie errors break the application
                // If we get persistent errors, disable tracking temporarily
                if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && (error.message.includes('Unterminated string') ||
                    error.message.includes('JSON') ||
                    error.message.includes('cookie'))) {
                    // Use safe logging to only show non-tracking-prevention errors
                    safeLogError('Detected corrupted cookie, disabling tracking temporarily', null);
                    sessionRef.current = null;
                    // Clear any corrupted cookies
                    try {
                        cookieService.clearAllCookies();
                    }
                    catch (clearError) {
                        // Use safe logging to only show non-tracking-prevention errors
                        safeLogError('Failed to clear corrupted cookies:', clearError);
                    }
                }
            }
        }
    }, [location.pathname, trackPageViews, trackSessionEvents]);
    // Track user interactions
    const trackInteraction = useCallback((interactionType: string, data?: any) => {
        if (trackUserInteractions && !trackingBlockedRef.current) {
            try {
                const event: TrackingEvent = {
                    type: interactionType,
                    data,
                    timestamp: Date.now()
                };
                eventQueueRef.current.push(event);
                // Update last activity
                lastActivityRef.current = Date.now();
                // Track session event
                if (trackSessionEvents) {
                    cookieService.trackSessionEvent(interactionType);
                }
                // Process high-priority events immediately
                if (['button_click', 'form_submit', 'video_play', 'video_pause'].includes(interactionType)) {
                    processEventQueue();
                }
                // Send high-priority interactions immediately to analytics service with enhanced context
                if (['button_click', 'form_submit', 'video_play', 'video_pause'].includes(interactionType)) {
                    try {
                        const sessionData = cookieService.getSessionData();
                        let behaviorData = null;
                        // Safely get user behavior data with error handling
                        try {
                            behaviorData = cookieService.getUserBehavior();
                        }
                        catch (behaviorError) {
                            logger.warn('Failed to get user behavior data for interaction:', { "arg0": behaviorError });
                            // Continue without behavior data
                        }
                        trackEvent({
                            action: interactionType,
                            category: 'user_interaction',
                            label: data?.context || interactionType,
                            custom_parameters: {
                                ...data,
                                timestamp: Date.now(),
                                source: 'cookie_tracking',
                                session_id: sessionData?.session_id,
                                session_duration: sessionData ? Date.now() - sessionData.start_time : 0,
                                page_views_count: behaviorData?.time_spent_on_site || 0,
                                time_on_site: behaviorData?.time_spent_on_site || 0,
                                user_preferences: cookieService.getUserPreferences()
                            }
                        });
                    }
                    catch (error) {
                        // Use safe logging to only show non-tracking-prevention errors
                        safeLogError('Failed to send enhanced interaction to analytics service:', error);
                    }
                }
            }
            catch (error) {
                // Check if this is a tracking prevention error
                if (error instanceof DOMException && (error.name === 'SecurityError' ||
                    error.message.includes('storage') ||
                    error.message.includes('blocked'))) {
                    trackingBlockedRef.current = true;
                    // Tracking prevention blocked - no logging needed
                    return;
                }
                // Use safe logging to only show non-tracking-prevention errors
                safeLogError('Failed to track interaction:', error);
            }
        }
    }, [trackUserInteractions, trackSessionEvents, processEventQueue]);
    // Track training progress
    const trackTrainingProgress = useCallback((courseId: string, progress: number, additionalData?: any) => {
        try {
            cookieService.trackTrainingProgress(courseId, progress);
            const event: TrackingEvent = {
                type: 'training_progress',
                data: { courseId, progress, ...additionalData },
                timestamp: Date.now()
            };
            eventQueueRef.current.push(event);
            // Update last activity
            lastActivityRef.current = Date.now();
            // Track session event
            if (trackSessionEvents) {
                cookieService.trackSessionEvent('training_progress');
            }
            // Send training progress to analytics service with enhanced context
            try {
                const sessionData = cookieService.getSessionData();
                let behaviorData = null;
                let trainingData = null;
                // Safely get user behavior data with error handling
                try {
                    behaviorData = cookieService.getUserBehavior();
                    trainingData = behaviorData?.training_progress;
                }
                catch (behaviorError) {
                    logger.warn('Failed to get user behavior data for training progress:', { "arg0": behaviorError });
                    // Continue without behavior data
                }
                trackEvent({
                    action: 'training_progress',
                    category: 'training',
                    label: courseId,
                    value: progress,
                    custom_parameters: {
                        course_id: courseId,
                        progress_percentage: progress,
                        ...additionalData,
                        timestamp: Date.now(),
                        source: 'cookie_tracking',
                        session_id: sessionData?.session_id,
                        session_duration: sessionData ? Date.now() - sessionData.start_time : 0,
                        total_training_progress: trainingData ? Object.keys(trainingData).length : 0,
                        time_on_site: behaviorData?.time_spent_on_site || 0,
                        user_preferences: cookieService.getUserPreferences()
                    }
                });
            }
            catch (error) {
                // Use safe logging to only show non-tracking-prevention errors
                safeLogError('Failed to send enhanced training progress to analytics service:', error);
            }
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track training progress:', error);
        }
    }, [trackSessionEvents]);
    // Track search queries
    const trackSearchQuery = useCallback((query: string, resultsCount?: number) => {
        if (trackSearchQueries && !trackingBlockedRef.current) {
            try {
                cookieService.trackSearchQuery(query);
                const event: TrackingEvent = {
                    type: 'search_query',
                    data: { query, resultsCount },
                    timestamp: Date.now()
                };
                eventQueueRef.current.push(event);
                // Update last activity
                lastActivityRef.current = Date.now();
                // Track session event
                if (trackSessionEvents) {
                    cookieService.trackSessionEvent('search_query');
                }
                // Send search query to analytics service with enhanced context
                try {
                    const sessionData = cookieService.getSessionData();
                    const behaviorData = cookieService.getUserBehavior();
                    const searchHistory = cookieService.getSearchHistory();
                    trackEvent({
                        action: 'search_query',
                        category: 'search',
                        label: query,
                        custom_parameters: {
                            search_query: query,
                            results_count: resultsCount,
                            timestamp: Date.now(),
                            source: 'cookie_tracking',
                            session_id: sessionData?.session_id,
                            session_duration: sessionData ? Date.now() - sessionData.start_time : 0,
                            search_history_count: searchHistory ? searchHistory.length : 0,
                            total_searches_this_session: behaviorData?.search_history?.length || 0,
                            time_on_site: behaviorData?.time_spent_on_site || 0,
                            user_preferences: cookieService.getUserPreferences()
                        }
                    });
                }
                catch (error) {
                    // Use safe logging to only show non-tracking-prevention errors
                    safeLogError('Failed to send enhanced search query to analytics service:', error);
                }
            }
            catch (error) {
                // Use safe logging to only show non-tracking-prevention errors
                safeLogError('Failed to track search query:', error);
            }
        }
    }, [trackSearchQueries, trackSessionEvents]);
    // Track preference changes
    const trackPreferenceChange = useCallback((category: string, key: string, value: any) => {
        try {
            const event: TrackingEvent = {
                type: 'preference_change',
                data: { category, key, value },
                timestamp: Date.now()
            };
            eventQueueRef.current.push(event);
            // Update last activity
            lastActivityRef.current = Date.now();
            // Track session event
            if (trackSessionEvents) {
                cookieService.trackSessionEvent('preference_change');
            }
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track preference change:', error);
        }
    }, [trackSessionEvents]);
    // Track content engagement
    const trackContentEngagement = useCallback((contentType: string, contentId: string, action: string, duration?: number) => {
        try {
            const event: TrackingEvent = {
                type: 'content_engagement',
                data: { contentType, contentId, action, duration },
                timestamp: Date.now()
            };
            eventQueueRef.current.push(event);
            // Update last activity
            lastActivityRef.current = Date.now();
            // Track session event
            if (trackSessionEvents) {
                cookieService.trackSessionEvent('content_engagement');
            }
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track content engagement:', error);
        }
    }, [trackSessionEvents]);
    // Track performance metrics
    const trackPerformance = useCallback((metric: string, value: number, unit?: string) => {
        try {
            const event: TrackingEvent = {
                type: 'performance',
                data: { metric, value, unit },
                timestamp: Date.now()
            };
            eventQueueRef.current.push(event);
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track performance:', error);
        }
    }, []);
    // Sync cookie data with backend analytics service
    const syncCookieDataWithBackend = useCallback(async () => {
        try {
            // Check if tracking is blocked before attempting sync
            if (trackingBlockedRef.current) {
                // Tracking prevention detected - no logging needed
                return;
            }
            // Test storage access before proceeding
            const testStorage = () => {
                try {
                    const testKey = '__sync_test__';
                    localStorage.setItem(testKey, 'test');
                    localStorage.removeItem(testKey);
                    return true;
                }
                catch (error) {
                    return false;
                }
            };
            if (!testStorage()) {
                // Storage access blocked - no logging needed
                trackingBlockedRef.current = true;
                return;
            }
            // Add a delay to reduce frequency of sync attempts
            if (trackingBlockedRef.current) {
                await new Promise(resolve => setTimeout(resolve, 5000)); // 5 second delay
                return;
            }
            const sessionData = cookieService.getSessionData();
            const behaviorData = cookieService.getUserBehavior();
            const preferencesData = cookieService.getUserPreferences();
            const personalizationData = cookieService.getPersonalizationData();
            if (sessionData && behaviorData) {
                // Send aggregated cookie data to backend analytics
                const syncData = {
                    session: {
                        session_id: sessionData.session_id,
                        start_time: sessionData.start_time,
                        last_activity: sessionData.last_activity,
                        page_views: sessionData.page_views,
                        duration: Date.now() - sessionData.start_time
                    },
                    behavior: {
                        total_page_views: behaviorData.last_visited_pages.length || 0,
                        time_spent_on_site: behaviorData.time_spent_on_site || 0,
                        search_queries: behaviorData.search_history || [],
                        training_progress: behaviorData.training_progress || {},
                        interaction_events: eventQueueRef.current.length
                    },
                    preferences: preferencesData,
                    personalization: personalizationData,
                    timestamp: Date.now()
                };
                // Track sync event to analytics service
                trackEvent({
                    action: 'cookie_data_sync',
                    category: 'analytics',
                    label: 'backend_sync',
                    custom_parameters: {
                        session_id: sessionData.session_id,
                        data_size: JSON.stringify(syncData).length,
                        source: 'cookie_tracking'
                    }
                });
                // Send to backend analytics service
                try {
                    // Get auth token if available for authenticated users
                    const token = await secureStorage.getEncrypted('auth_token');
                    const headers: Record<string, string> = {
                        'Content-Type': 'application/json'
                    };
                    // Include auth token if available (works for both authenticated and anonymous users)
                    if (token) {
                        headers['Authorization'] = `Bearer ${token}`;
                    }
                    const response = await fetch('/api/analytics/sync-cookie-data', {
                        method: 'POST',
                        headers,
                        body: JSON.stringify(syncData)
                    });
                    if (response.ok) {
                        const result = await response.json();
                        // Cookie data synced successfully
                    }
                    else {
                        // Use enhanced logging to handle tracking prevention gracefully
                        enhancedLogError('Failed to sync cookie data:', { status: response.status, statusText: response.statusText }, 'useCookieTracking');
                    }
                }
                catch (apiError) {
                    // Handle different types of API errors
                    if (apiError instanceof TypeError && apiError.message.includes('fetch')) {
                        // Use safe logging to only show non-tracking-prevention errors
                        safeLogError('Network error during cookie sync - may be offline:', apiError);
                    }
                    else {
                        // Use safe logging to only show non-tracking-prevention errors
                        safeLogError('API call failed for cookie sync:', apiError);
                    }
                }
            }
        }
        catch (error) {
            // Check if this is a tracking prevention error
            if (error instanceof DOMException && (error.name === 'SecurityError' ||
                error.message.includes('storage') ||
                error.message.includes('blocked'))) {
                // Tracking prevention blocked - no logging needed
            }
            else {
                // Use safe logging to only show non-tracking-prevention errors
                safeLogError('Failed to sync cookie data with backend:', error);
            }
        }
    }, []);
    // Update session periodically
    useEffect(() => {
        const interval = setInterval(() => {
            if (sessionRef.current) {
                try {
                    const now = Date.now();
                    const timeSinceLastActivity = now - lastActivityRef.current;
                    // Update session if there's been activity in the last 30 minutes
                    if (timeSinceLastActivity < 30 * 60 * 1000) {
                        cookieService.updateSession({
                            last_activity: now
                        });
                    }
                    // Process event queue every 30 seconds
                    processEventQueue();
                    // Sync cookie data with backend analytics every 5 minutes
                    if (now % (5 * 60 * 1000) < 30000) { // Every 5 minutes
                        syncCookieDataWithBackend();
                    }
                }
                catch (error) {
                    // Use safe logging to only show non-tracking-prevention errors
                    safeLogError('Failed to update session:', error);
                }
            }
        }, 30000); // 30 seconds
        return () => clearInterval(interval);
    }, [processEventQueue, syncCookieDataWithBackend]);
    // Track page visibility changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            try {
                if (document.hidden) {
                    trackInteraction('page_hidden');
                }
                else {
                    trackInteraction('page_visible');
                }
            }
            catch (error) {
                // Use safe logging to only show non-tracking-prevention errors
                safeLogError('Failed to track visibility change:', error);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [trackInteraction]); // trackInteraction is stable, no need for dependency
    // Track window focus/blur
    useEffect(() => {
        const handleFocus = () => {
            try {
                trackInteraction('window_focus');
            }
            catch (error) {
                // Use safe logging to only show non-tracking-prevention errors
                safeLogError('Failed to track window focus:', error);
            }
        };
        const handleBlur = () => {
            try {
                trackInteraction('window_blur');
            }
            catch (error) {
                // Use safe logging to only show non-tracking-prevention errors
                safeLogError('Failed to track window blur:', error);
            }
        };
        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);
        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
        };
    }, [trackInteraction]); // trackInteraction is stable, no need for dependency
    // Track scroll depth
    useEffect(() => {
        let maxScrollDepth = 0;
        let scrollTimeout: NodeJS.Timeout;
        const handleScroll = () => {
            try {
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
                const scrollPercentage = Math.round((scrollTop / scrollHeight) * 100);
                if (scrollPercentage > maxScrollDepth) {
                    maxScrollDepth = scrollPercentage;
                    // Track scroll depth milestones
                    if ([25, 50, 75, 90, 100].includes(maxScrollDepth)) {
                        trackInteraction('scroll_depth', { depth: maxScrollDepth });
                    }
                }
                // Clear existing timeout
                clearTimeout(scrollTimeout);
                // Set new timeout to track scroll end
                scrollTimeout = setTimeout(() => {
                    try {
                        trackInteraction('scroll_end', { maxDepth: maxScrollDepth });
                    }
                    catch (error) {
                        // Use safe logging to only show non-tracking-prevention errors
                        safeLogError('Failed to track scroll end:', error);
                    }
                }, 1000);
            }
            catch (error) {
                // Use safe logging to only show non-tracking-prevention errors
                safeLogError('Failed to track scroll:', error);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
        };
    }, [trackInteraction]); // trackInteraction is stable, no need for dependency
    // Track time spent on page
    useEffect(() => {
        const startTime = Date.now();
        const handleBeforeUnload = () => {
            try {
                const timeSpent = Date.now() - startTime;
                trackInteraction('page_exit', { timeSpent });
                // Update total time spent on site
                const behavior = cookieService.getUserBehavior();
                cookieService.updateUserBehavior({
                    time_spent_on_site: behavior.time_spent_on_site + timeSpent
                });
            }
            catch (error) {
                // Use safe logging to only show non-tracking-prevention errors
                safeLogError('Failed to track page exit:', error);
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [trackInteraction]); // trackInteraction is stable, no need for dependency
    // Get current tracking data
    const getTrackingData = useCallback(() => {
        try {
            return {
                session: cookieService.getSessionData(),
                behavior: cookieService.getUserBehavior(),
                preferences: cookieService.getUserPreferences(),
                personalization: cookieService.getPersonalizationData(),
                analytics: cookieService.getAnalyticsData()
            };
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to get tracking data:', error);
            return {
                session: null,
                behavior: null,
                preferences: null,
                personalization: null,
                analytics: null
            };
        }
    }, []);
    // Update user behavior
    const updateBehavior = useCallback((updates: any) => {
        try {
            cookieService.updateUserBehavior(updates);
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to update behavior:', error);
        }
    }, []);
    // Update personalization
    const updatePersonalization = useCallback((updates: any) => {
        try {
            cookieService.updatePersonalization(updates);
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to update personalization:', error);
        }
    }, []);
    // Cleanup tracking data
    const cleanupTrackingData = useCallback(() => {
        try {
            cookieService.cleanupExpiredData();
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to cleanup tracking data:', error);
        }
    }, []);
    return {
        // Tracking functions
        trackInteraction,
        trackTrainingProgress,
        trackSearchQuery,
        trackPreferenceChange,
        trackContentEngagement,
        trackPerformance,
        // Data access
        getTrackingData,
        updateBehavior,
        updatePersonalization,
        // Utility functions
        cleanupTrackingData,
        processEventQueue,
        syncCookieDataWithBackend,
        // Current state
        session: sessionRef.current,
        lastActivity: lastActivityRef.current,
        eventQueue: eventQueueRef.current
    };
};
// Hook for tracking specific user interactions
export const useInteractionTracking = () => {
    const { trackInteraction } = useCookieTracking({ trackUserInteractions: true });
    const trackButtonClick = useCallback((buttonId: string, buttonText?: string, context?: string) => {
        try {
            trackInteraction('button_click', { buttonId, buttonText, context });
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track button click:', error);
        }
    }, [trackInteraction]);
    const trackFormSubmit = useCallback((formId: string, formData?: any) => {
        try {
            trackInteraction('form_submit', { formId, formData });
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track form submit:', error);
        }
    }, [trackInteraction]);
    const trackLinkClick = useCallback((linkUrl: string, linkText?: string) => {
        try {
            trackInteraction('link_click', { linkUrl, linkText });
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track link click:', error);
        }
    }, [trackInteraction]);
    const trackVideoInteraction = useCallback((videoId: string, action: 'play' | 'pause' | 'seek' | 'complete', timestamp?: number) => {
        try {
            trackInteraction('video_interaction', { videoId, action, timestamp });
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track video interaction:', error);
        }
    }, [trackInteraction]);
    const trackModalInteraction = useCallback((modalId: string, action: 'open' | 'close') => {
        try {
            trackInteraction('modal_interaction', { modalId, action });
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track modal interaction:', error);
        }
    }, [trackInteraction]);
    return {
        trackButtonClick,
        trackFormSubmit,
        trackLinkClick,
        trackVideoInteraction,
        trackModalInteraction
    };
};
// Hook for tracking training-specific interactions
export const useTrainingTracking = () => {
    const { trackTrainingProgress, trackContentEngagement } = useCookieTracking({ enableTrainingProgress: true });
    const trackCourseStart = useCallback((courseId: string, courseName?: string) => {
        try {
            trackContentEngagement('course', courseId, 'start');
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track course start:', error);
        }
    }, [trackContentEngagement]);
    const trackCourseComplete = useCallback((courseId: string, courseName?: string, score?: number) => {
        try {
            trackContentEngagement('course', courseId, 'complete', score);
            trackTrainingProgress(courseId, 100, { courseName, score });
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track course complete:', error);
        }
    }, [trackContentEngagement, trackTrainingProgress]);
    const trackLessonView = useCallback((lessonId: string, lessonName?: string, duration?: number) => {
        try {
            trackContentEngagement('lesson', lessonId, 'view', duration);
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track lesson view:', error);
        }
    }, [trackContentEngagement]);
    const trackQuizAttempt = useCallback((quizId: string, score: number, totalQuestions: number) => {
        try {
            trackContentEngagement('quiz', quizId, 'attempt', score);
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track quiz attempt:', error);
        }
    }, [trackContentEngagement]);
    const trackCertificateEarned = useCallback((certificateId: string, courseId: string) => {
        try {
            trackContentEngagement('certificate', certificateId, 'earned', 0);
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track certificate earned:', error);
        }
    }, [trackContentEngagement]);
    return {
        trackCourseStart,
        trackCourseComplete,
        trackLessonView,
        trackQuizAttempt,
        trackCertificateEarned,
        trackTrainingProgress
    };
};
// Hook for tracking search and discovery
export const useSearchTracking = () => {
    const { trackSearchQuery } = useCookieTracking({ trackSearchQueries: true });
    const trackSearch = useCallback((query: string, resultsCount?: number, filters?: any) => {
        try {
            trackSearchQuery(query, resultsCount);
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track search:', error);
        }
    }, [trackSearchQuery]);
    const trackFilterUsage = useCallback((filterType: string, filterValue: any) => {
        try {
            // This would be tracked as a regular interaction
            // You could extend this to track search-specific interactions
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track filter usage:', error);
        }
    }, []);
    const trackResultClick = useCallback((resultId: string, resultType: string, position: number) => {
        try {
            // This would be tracked as a regular interaction
            // You could extend this to track search-specific interactions
        }
        catch (error) {
            // Use safe logging to only show non-tracking-prevention errors
            safeLogError('Failed to track result click:', error);
        }
    }, []);
    return {
        trackSearch,
        trackFilterUsage,
        trackResultClick
    };
};
